// lib/slides/blockArtwork.ts
//
// The RDM stamp library: the Westfield cartouche, Better Places, Tenant Survey
// and the mini stamp, copied from Thibaut's first-page mock-up (2026-10-06).
// Each is ONE page element that moves and resizes as a unit, and every cell of
// it is a separately editable text ("toutes les cases individuellement
// éditables"). Like the validation card, the editor and the PDF draw the same
// artwork from here, in page points, with the PDF's standard fonts.

import artwork from './assets/westfieldCartouche.json';
import sheet from './assets/rdmStamps.json';
import type { SlideBlockKind } from '#types/types';
import { ANTON, measureStandardText, standardDisplayText, standardFontFor, type SlideFont } from './standardText';
import { STAMP_LINE_HEIGHT, wrapStampText } from './stampLayout';
import { STAMP_COLORS, STATUS_CELL_COLOR, VERDICT_MARK } from './validationPoints';

export interface BlockText { text: string; x: number; y: number; size: number; font: SlideFont; color: string; width: number; field?: string }
export interface BlockField { key: string; label: string; value: string; display: string; x: number; y: number; w: number; h: number; size: number; font: SlideFont; align: 'left' | 'center' }
export interface BlockArtwork {
  rects: Array<{ x: number; y: number; w: number; h: number; fill?: string; stroke?: string; strokeWidth?: number }>;
  paths: Array<{ d: string; color: string; width: number }>;
  texts: BlockText[];
  fields: BlockField[];
  /** The Westfield wordmark, drawn from its vector outline. */
  logos: Array<{ x: number; y: number; width: number; height: number }>;
  overflow: boolean;
}

export const WESTFIELD_LOGO = artwork.logo;
const LOGO_RATIO = 85.51 / 27.22;
const GREEN = '#74A99A', GREEN_TEXT = '#5E9284', SURVEY_BLUE = '#2F6BFF';

export interface BlockContext {
  language?: 'en' | 'fr';
  deckName?: string;
  /** The tenant the brand research identified. */
  brand?: string;
  round?: number;
  header?: { rdm?: string; cellule?: string; centre?: string };
  pageCount?: number;
  now?: Date;
}

const formatDate = (now: Date, language?: 'en' | 'fr') => language === 'fr'
  ? now.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.')
  // English decks go to the US too, where 05/10 reads as May: spell the month.
  : now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

// URW's stamp sheet, "2026_RDM Stamps Europe_10Points_FINAL" (Thibaut Aulanier, received 2026-10-13),
// copied word for word into assets/rdmStamps.json: the "Further stamps" and "Kiosks + F&B" tabs.
// The sheet writes them in English only, so they stay English in a French deck.
export type StampGroup = 'cover' | 'comment' | 'approval' | 'note';
export const STAMP_GROUP_LABELS: Record<StampGroup, string> = { cover: 'Review cover', comment: 'Comment only', approval: 'Approval stamps', note: 'Notes' };
const titled = (label: string, group: StampGroup, [title, body]: string[], h: number) =>
  ({ label, group, size: { w: 361, h }, defaults: () => ({ title: title!, body: body! }) });
const untitled = (label: string, group: StampGroup, body: string, w: number, h: number) =>
  ({ label, group, size: { w, h }, defaults: () => ({ body }) });
const GREY_CELL = VERDICT_MARK.na.color;

export const BLOCK_LIBRARY: Record<SlideBlockKind, {
  label: string;
  group: StampGroup;
  /** Size on the A4-landscape cover page, in points. */
  size: { w: number; h: number };
  defaults: (ctx: BlockContext) => Record<string, string>;
}> = {
  cartouche: {
    label: 'Westfield cartouche',
    group: 'cover',
    size: { w: 766, h: 68 },
    defaults: ({ language, deckName, round, header, pageCount, now = new Date() }) => ({
      title: deckName || (language === 'fr' ? 'TITRE' : 'TITLE'),
      subtitle: language === 'fr' ? 'Revue du concept design' : 'Concept design review',
      date: formatDate(now, language),
      rdm: `RDM : ${header?.rdm ?? ''}`.trim(),
      rev: `REV. ${round ?? 1}`,
      rd: 'RD. 1',
      page: `PAGE : 1/${pageCount ?? 1}`,
    }),
  },
  betterPlaces: {
    label: 'Better Places',
    group: 'cover',
    size: { w: 361, h: 318 },
    defaults: () => ({
      title: 'BETTERPLACES',
      materials: 'MATERIALS: An eco-design approach is encouraged in respect of any works. Materials used are required to:\n- be low carbon\n- preserve indoor air quality (low VOC materials)\n- have a low environmental impact generally\n- (where wood is used) be certified under FSC or PEFC labels',
      lighting: 'LIGHTING: "LED" lighting (or any alternative technology allowing a reduction of energy consumption at least equivalent to "LED" lighting) to be used for all new installations and changes of existing lighting equipment. Newly installed light sources are not to exceed a lighting power rate per square metre within the sales area of the Premises (all lighting combined: ceiling, furniture, etc.) of:\n• 20 W/sq. m. for smaller premises/units (below 300 sq. m.);\n• 15 W/sq. m. for larger premises/units (above 300 sq. m.);\n• 25 W/sq. m. for restaurants.',
      digital: 'DIGITAL: Ensure all digital screens and lightboxes are turned off at the end of each trading day.',
      evidence: 'Evidence of compliance with these requirements will be provided to the Landlord.',
    }),
  },
  tenantSurvey: {
    label: 'Tenant Survey',
    group: 'cover',
    size: { w: 361, h: 66 },
    defaults: () => ({
      title: 'TENANT SURVEY',
      body: "All dimensions and site conditions are subject to a site survey.\nIt is the tenant's designer / contractor's responsibility to carry out a full site survey prior to the commencement of any manufacture or build works.",
    }),
  },
  ministamp: {
    label: 'Comment only (mini stamp)',
    group: 'comment',
    size: { w: 260, h: 110 },
    defaults: ({ language, deckName, round, header, now = new Date() }) => language === 'fr' ? {
      title: "VALIDATION DU CONCEPT DESIGN & DE LA ZONE D'ENTREE DE 3m",
      rdm: `RDM : ${header?.rdm ?? ''}`.trim(), unit: `CELLULE : ${header?.cellule || deckName || ''}`.trim(),
      centre: `CENTRE : ${header?.centre ?? ''}`.trim(), issue: `RETOUR : ${round ?? 1}`,
      viewed: `VU LE : ${formatDate(now, language)}`,
      disclaimer: 'POUR COMMENTAIRES UNIQUEMENT\nCECI N’EST PAS UNE VALIDATION',
    } : {
      title: 'CONCEPT DESIGN VALIDATION & 3m SHOPFRONT ZONE',
      rdm: `RDM: ${header?.rdm ?? ''}`.trim(), unit: `UNIT: ${header?.cellule || deckName || ''}`.trim(),
      centre: `CENTRE: ${header?.centre ?? ''}`.trim(), issue: `ISSUE: ${round ?? 1}`,
      viewed: `VIEWED ON: ${formatDate(now, language)}`,
      disclaimer: 'FOR COMMENT ONLY\nTHIS IS NOT AN APPROVAL',
    },
  },
  materialBoard: untitled('Digital material board', 'comment', sheet.materialBoard, 260, 82),
  signage: untitled('Signage drawings', 'comment', sheet.signage, 260, 82),
  exposedCeilings: untitled('Exposed ceilings note', 'comment', sheet.exposedCeilings, 260, 62),
  lightingCeiling: untitled('Lighting ceiling details', 'comment', sheet.lightingCeiling, 260, 52),
  flooring: untitled('Flooring details', 'comment', sheet.flooring, 260, 52),
  siteSpecific: titled('Site specific', 'approval', sheet.siteSpecific, 92),
  limitedTime: titled('Limited time approval', 'approval', sheet.limitedTime, 80),
  conditionOfApproval: titled('Condition of approval', 'approval', sheet.conditionOfApproval, 56),
  rollerShutter: titled('Conditional roller shutter approval', 'approval', sheet.rollerShutter, 120),
  digitalContent: titled('Digital content approval', 'approval', sheet.digitalContent, 130),
  fbKiosk: titled('F&B kiosks - small items', 'approval', sheet.fbKiosk, 88),
  kioskValidation: {
    label: 'Kiosk & F&B validation stamp',
    group: 'approval',
    size: { w: 300, h: 250 },
    defaults: ({ deckName, brand, round, header, now = new Date() }) => ({
      title: sheet.kiosk.title, heading: sheet.kiosk.heading, status: 'Status:',
      rdm: `RDM: ${header?.rdm ?? ''}`.trim(), unit: `Unit: ${header?.cellule || brand || deckName || ''}`.trim(),
      date: `Date: ${formatDate(now)}`, centre: `Centre: ${header?.centre ?? ''}`.trim(), issue: `Issue: ${round ?? 1}`,
      a: sheet.kiosk.a, b: sheet.kiosk.b, c: sheet.kiosk.c, footer: sheet.kiosk.footer,
    }),
  },
  noteLed: untitled('All lighting LED', 'note', sheet.noteLed, 300, 30),
  noteLogoHoles: untitled('Previous logo holes repaired', 'note', sheet.noteLogoHoles, 300, 46),
  noteCarpets: untitled('Carpets and rugs inset', 'note', sheet.noteCarpets, 300, 52),
  seductiveDisclaimer: titled('Disclaimer for seductive dossiers', 'note', sheet.seductiveDisclaimer, 100),
};

/** Initials as the RDM cells hold them: "Thibaut AULANIER" -> "TA". */
export const initialsOf = (name: string | null | undefined): string =>
  (name ?? '').normalize('NFC').split(/[\s-]+/).filter(Boolean).slice(0, 3).map((word) => word[0]!.toUpperCase()).join('');

export const BLOCK_KINDS = Object.keys(BLOCK_LIBRARY) as SlideBlockKind[];

/** The four library stamps on Thibaut's cover page, in A4-landscape points. */
export const COVER_PAGE = { width: 842, height: 595 } as const;
export const COVER_LAYOUT = {
  stamp: { x: 37.5, y: 51, w: 358, h: 414 },
  betterPlaces: { x: 444.5, y: 52.5, w: 361, h: 318 },
  tenantSurvey: { x: 449, y: 396.5, w: 361, h: 66 },
  cartouche: { x: 37.5, y: 510.5, w: 766, h: 68 },
} as const;

function draft(fields: Record<string, string>, labels: Record<string, string>) {
  const art: BlockArtwork = { rects: [], paths: [], texts: [], fields: [], logos: [], overflow: false };
  const font = (bold: boolean): SlideFont => standardFontFor('sans', bold);
  const push = (text: string, x: number, y: number, size: number, face: SlideFont, color: string, field?: string) =>
    art.texts.push({ text, x, y, size, font: face, color, width: measureStandardText(text, size, face), field });
  const wrap = (text: string, size: number, face: SlideFont, w: number) =>
    wrapStampText(standardDisplayText(text, face), size, false, Math.max(0, w), (t, s) => measureStandardText(t, s, face));
  /** One editable cell: its text wrapped and centred vertically in the box. */
  const cell = (key: string, x: number, y: number, w: number, h: number, size: number, bold: boolean | SlideFont, color: string, align: 'left' | 'center' = 'left', maxLines = Infinity) => {
    const face = typeof bold === 'boolean' ? font(bold) : bold;
    const value = fields[key] ?? '';
    const lines = value ? wrap(value, size, face, w) : [];
    const step = size * STAMP_LINE_HEIGHT;
    if (lines.length > maxLines || lines.length * step > h + 1e-7 || lines.some(l => measureStandardText(l, size, face) > w + 1e-7)) art.overflow = true;
    const top = y + h / 2 - lines.length * step / 2 + size * 0.85;
    lines.forEach((line, i) => push(line, x + (align === 'center' ? (w - measureStandardText(line, size, face)) / 2 : 0), top + i * step, size, face, color, key));
    art.fields.push({ key, label: labels[key] ?? key, value, display: lines.join('\n'), x, y, w, h, size, font: face, align });
  };
  /** Paragraph flowing down from `y`; a leading "LABEL:" is set in bold, as on the mock-up. */
  const paragraph = (key: string, x: number, y: number, w: number, size: number, color: string, labelColor: string) => {
    const value = fields[key] ?? '';
    const step = size * STAMP_LINE_HEIGHT;
    const [first = '', ...rest] = standardDisplayText(value).split('\n');
    const label = /^([A-Z][A-Z ]*:)\s*/.exec(first);
    const lines: Array<{ text: string; lead?: string }> = [];
    if (label) {
      const leadW = measureStandardText(label[1] + ' ', size, font(true));
      const words = first.slice(label[0].length).split(/\s+/).filter(Boolean);
      let line = '';
      while (words.length && measureStandardText(line ? `${line} ${words[0]}` : words[0]!, size, font(false)) <= w - leadW) line = line ? `${line} ${words.shift()}` : words.shift()!;
      lines.push({ text: line, lead: label[1] });
      if (words.length) lines.push(...wrap(words.join(' '), size, font(false), w).map(text => ({ text })));
    } else if (first) lines.push(...wrap(first, size, font(false), w).map(text => ({ text })));
    for (const extra of rest) lines.push(...(extra ? wrap(extra, size, font(false), w) : ['']).map(text => ({ text })));
    lines.forEach((line, i) => {
      const baseline = y + size * 0.85 + i * step;
      if (line.lead) {
        push(line.lead, x, baseline, size, font(true), labelColor, key);
        push(line.text, x + measureStandardText(line.lead + ' ', size, font(true)), baseline, size, font(false), color, key);
      } else push(line.text, x, baseline, size, font(false), color, key);
      if (measureStandardText(line.text, size, font(false)) > w + 1e-7) art.overflow = true;
    });
    const h = lines.length * step;
    art.fields.push({ key, label: labels[key] ?? key, value, display: lines.map(l => (l.lead ? `${l.lead} ` : '') + l.text).join('\n'), x, y, w, h: Math.max(h, step), size, font: font(false), align: 'left' });
    return h;
  };
  return { art, cell, paragraph, push, wrap };
}

const BODY = { body: 'Text' }, TITLED = { title: 'Title', body: 'Text' };

export const BLOCK_FIELD_LABELS: Record<SlideBlockKind, Record<string, string>> = {
  cartouche: { title: 'Title', subtitle: 'Subtitle', date: 'Date', rdm: 'RDM', rev: 'Revision', rd: 'Analysis phase', page: 'Page' },
  betterPlaces: { title: 'Better Places title', materials: 'Materials', lighting: 'Lighting', digital: 'Digital', evidence: 'Evidence' },
  tenantSurvey: { title: 'Tenant Survey title', body: 'Tenant Survey text' },
  siteSpecific: TITLED,
  materialBoard: BODY, signage: BODY, exposedCeilings: BODY, lightingCeiling: BODY, flooring: BODY,
  noteLed: BODY, noteLogoHoles: BODY, noteCarpets: BODY,
  limitedTime: TITLED, conditionOfApproval: TITLED, rollerShutter: TITLED, digitalContent: TITLED, fbKiosk: TITLED, seductiveDisclaimer: TITLED,
  kioskValidation: { title: 'Title', heading: 'Heading', status: 'Status (A, B or C)', rdm: 'RDM', unit: 'Unit', date: 'Date', centre: 'Centre', issue: 'Issue', a: 'A', b: 'B', c: 'C', footer: 'Footer' },
  ministamp: { title: 'Mini stamp title', rdm: 'RDM', unit: 'Unit', centre: 'Centre', issue: 'Issue', viewed: 'Viewed on', disclaimer: 'Disclaimer' },
};

/** Lays the block out at font size `fs`; `height` is what the content needs. */
function build(kind: SlideBlockKind, fields: Record<string, string>, W: number, H: number, fs: number) {
  // A kind retired from the library (the draft "3 m zone sections") keeps drawing its own text as a framed box.
  const labels = BLOCK_FIELD_LABELS[kind] ?? Object.fromEntries(Object.keys(fields).map(key => [key, key]));
  const d = draft(fields, labels);
  const { art, cell, paragraph } = d;
  let height = H;
  if (kind === 'cartouche') {
    const cols = [0, 0.15, 0.694, 0.773, 0.857, 1].map(f => f * W), mid = H / 2, pad = fs * 0.6;
    art.rects.push({ x: 0, y: 0, w: W, h: H, fill: '#FFFFFF', stroke: '#111111', strokeWidth: 1.4 });
    art.paths.push({ d: `M ${cols[1]} 0 V ${H} M ${cols[1]} ${mid} H ${W} M ${cols[4]} 0 V ${H} M ${cols[2]} 0 V ${H} M ${cols[3]} ${mid} V ${H}`, color: '#111111', width: 1.1 });
    const logoW = Math.min((cols[1]! - 2 * pad), (H - 2 * pad) * LOGO_RATIO);
    art.logos.push({ x: (cols[1]! - logoW) / 2, y: (H - logoW / LOGO_RATIO) / 2, width: logoW, height: logoW / LOGO_RATIO });
    // The short cells shrink rather than break "REV. 2" over two lines.
    const c = (key: string, from: number, to: number, row: 0 | 1, size: number, bold: boolean | SlideFont, align: 'left' | 'center' = 'left') =>
      cell(key, cols[from]! + pad, row * mid, cols[to]! - cols[from]! - 2 * pad, mid, size, bold, '#111111', align, from >= 2 ? 1 : Infinity);
    c('title', 1, 2, 0, fs * 1.45, ANTON); c('subtitle', 1, 2, 1, fs, false);
    c('date', 2, 4, 0, fs, false, 'center'); c('rdm', 4, 5, 0, fs, false);
    c('rev', 2, 3, 1, fs, false); c('rd', 3, 4, 1, fs, false); c('page', 4, 5, 1, fs, false);
  } else if (kind === 'betterPlaces') {
    const headH = fs * 3, pad = fs * 0.5;
    art.rects.push({ x: 0, y: 0, w: W, h: H, fill: '#FFFFFF', stroke: GREEN, strokeWidth: 1.2 });
    art.rects.push({ x: 0, y: 0, w: W, h: headH, fill: GREEN });
    cell('title', pad, 0, W - 2 * pad, headH, fs * 1.9, true, '#FFFFFF', 'center');
    let y = headH + pad;
    for (const key of ['materials', 'lighting', 'digital', 'evidence']) y += paragraph(key, pad, y, W - 2 * pad, fs, GREEN_TEXT, GREEN_TEXT) + fs * 0.9;
    height = y - fs * 0.9 + pad;
  } else if (kind === 'kioskValidation') {
    const pad = fs * 0.5, headH = fs * 3, rowH = fs * 2, letterW = W * 0.13, bold = standardFontFor('sans', true);
    // The sheet prints the three statuses as a legend; naming one in the status cell fades the other two.
    const chosen = /\b([ABC])\b/.exec((fields.status ?? '').toUpperCase().replace(/^STATUS:?/, ''))?.[1];
    art.rects.push({ x: 0, y: 0, w: W, h: H, fill: '#FFFFFF', stroke: STAMP_COLORS.blue, strokeWidth: 1.2 });
    art.rects.push({ x: 0, y: 0, w: W, h: headH, fill: STAMP_COLORS.blue });
    cell('title', pad, 0, W - 2 * pad, headH, fs * 1.2, true, '#FFFFFF', 'center');
    let y = headH;
    for (const keys of [['rdm', 'unit', 'date'], ['centre', 'issue']]) {
      const colW = W / keys.length;
      keys.forEach((key, i) => cell(key, i * colW + pad, y, colW - 2 * pad, rowH, fs * 0.9, false, STAMP_COLORS.text, 'left', 2));
      y += rowH;
      art.paths.push({ d: `M 0 ${y} H ${W}`, color: STAMP_COLORS.grid, width: 0.8 });
    }
    art.rects.push({ x: 0, y, w: W, h: rowH, fill: STAMP_COLORS.band });
    cell('heading', pad, y, W * 0.6, rowH, fs, true, STAMP_COLORS.text);
    cell('status', W * 0.62, y, W * 0.38 - pad, rowH, fs, true, STAMP_COLORS.text, 'left', 1);
    y += rowH;
    for (const letter of ['A', 'B', 'C'] as const) {
      const key = letter.toLowerCase(), faded = !!chosen && chosen !== letter, size = fs * 0.9;
      const lines = d.wrap(fields[key] ?? '', size, standardFontFor('sans', false), W - letterW - 2 * pad).length;
      const h = Math.max(rowH * 1.3, lines * size * STAMP_LINE_HEIGHT + fs * 0.8);
      art.rects.push({ x: 0, y, w: letterW, h, fill: faded ? GREY_CELL : STATUS_CELL_COLOR[letter] });
      d.push(letter, (letterW - measureStandardText(letter, fs * 1.6, bold)) / 2, y + h / 2 + fs * 0.55, fs * 1.6, bold, '#FFFFFF');
      cell(key, letterW + pad, y, W - letterW - 2 * pad, h, size, false, faded ? STAMP_COLORS.footer : STAMP_COLORS.text);
      y += h;
      art.paths.push({ d: `M 0 ${y} H ${W}`, color: STAMP_COLORS.grid, width: 0.8 });
    }
    height = y + pad + paragraph('footer', pad, y + pad, W - 2 * pad, fs * 0.62, STAMP_COLORS.footer, STAMP_COLORS.footer) + pad;
  } else if (!('title' in labels)) {
    // The sheet's untitled capsules and notes: a framed text, nothing else.
    const pad = fs * 0.45;
    art.rects.push({ x: 0, y: 0, w: W, h: H, fill: '#FFFFFF', stroke: SURVEY_BLUE, strokeWidth: 1.2 });
    height = pad + paragraph('body', pad, pad, W - 2 * pad, fs, SURVEY_BLUE, SURVEY_BLUE) + pad;
  } else if (kind !== 'ministamp') {
    const headH = fs * 2.1, pad = fs * 0.35;
    art.rects.push({ x: 0, y: 0, w: W, h: H, fill: '#FFFFFF', stroke: SURVEY_BLUE, strokeWidth: 1.2 });
    art.paths.push({ d: `M 0 ${headH} H ${W}`, color: SURVEY_BLUE, width: 1 });
    cell('title', pad, 0, W - 2 * pad, headH, fs * 1.5, true, SURVEY_BLUE, 'center');
    height = headH + pad + paragraph('body', pad, headH + pad, W - 2 * pad, fs, SURVEY_BLUE, SURVEY_BLUE) + pad;
  } else {
    const pad = fs * 0.5, headH = fs * 3.4, rowH = fs * 2, half = W * 0.55;
    art.rects.push({ x: 0, y: 0, w: W, h: H, fill: '#FFFFFF', stroke: STAMP_COLORS.blue, strokeWidth: 1.2 });
    art.rects.push({ x: 0, y: 0, w: W, h: headH, fill: STAMP_COLORS.blue });
    cell('title', pad, 0, W - 2 * pad, headH, fs * 1.05, true, '#FFFFFF', 'center');
    const rows: Array<[string, string?]> = [['rdm', 'unit'], ['centre', 'issue'], ['viewed']];
    rows.forEach(([a, b], i) => {
      const y = headH + i * rowH;
      art.paths.push({ d: `M 0 ${y + rowH} H ${W}`, color: STAMP_COLORS.grid, width: 0.8 });
      cell(a, pad, y, (b ? half : W) - 2 * pad, rowH, fs, false, STAMP_COLORS.text);
      if (b) cell(b, half + pad, y, W - half - 2 * pad, rowH, fs, false, STAMP_COLORS.text);
    });
    const top = headH + rows.length * rowH;
    cell('disclaimer', pad, top, W - 2 * pad, H - top, fs * 1.05, true, '#D9291C', 'center');
    height = Math.max(H, top + fs * 3.2);
  }
  return { art, height };
}

/** Largest font, up to the box's natural size, whose layout fits the box. */
export function buildBlockArtwork(kind: SlideBlockKind, fields: Record<string, string>, w: number, h: number): BlockArtwork {
  const base = kind === 'cartouche' ? Math.min(h * 0.2, w * 0.02) : w * 0.034;
  const fits = (fs: number) => { const r = build(kind, fields, w, h, fs); return { ...r, ok: r.height <= h + 1e-7 && !r.art.overflow }; };
  let best = fits(base);
  if (best.ok) return best.art;
  let lo = 1, hi = base;
  best = fits(lo);
  if (!best.ok) return best.art;
  for (let i = 0; i < 12 && hi - lo > 0.01; i++) {
    const mid = (lo + hi) / 2, candidate = fits(mid);
    if (candidate.ok) { best = candidate; lo = mid; } else hi = mid;
  }
  return best.art;
}

/** SVG markup of the Westfield wordmark inside `box`, for the editor. */
export const westfieldLogoTransform = (box: { x: number; y: number; width: number }) => {
  const [vx, vy, vw] = WESTFIELD_LOGO.viewBox.split(' ').map(Number) as [number, number, number];
  const scale = box.width / vw;
  return { scale, translateX: box.x - vx * scale, translateY: box.y - vy * scale };
};
